/** XeAgents.ai **/
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

export interface HealthcareSettings {
  id: string;
  clinicName: string;
  country: string;
  timezone: string;
  transferNumber: string | null;
  emergencyNumber: string | null;
  businessHours: Record<string, { start: string; end: string } | null> | null;
  connectorType: "internal" | "rest" | "cliniko";
  connectorConfig: Record<string, any> | null;
  hasConnectorSecret: boolean;
  agentIds: string[];
  confirmationSms?: boolean;
  /** The call handling the clinic saved, and what calls really use (defaults filled in). */
  inboundProfile?: Record<string, any> | null;
  inboundProfileEffective?: Record<string, any>;
  registrationFields?: Array<{ key: string; label: string; type: string; required: boolean; options?: string[]; hint?: string }> | null;
}

const COUNTRIES = [
  { code: "AU", name: "Australia", tz: "Australia/Sydney", emergency: "000" },
  { code: "IN", name: "India", tz: "Asia/Kolkata", emergency: "112" },
  { code: "NZ", name: "New Zealand", tz: "Pacific/Auckland", emergency: "111" },
  { code: "GB", name: "United Kingdom", tz: "Europe/London", emergency: "999" },
  { code: "IE", name: "Ireland", tz: "Europe/Dublin", emergency: "112" },
  { code: "US", name: "United States", tz: "America/New_York", emergency: "911" },
  { code: "CA", name: "Canada", tz: "America/Toronto", emergency: "911" },
];

const DAYS: Array<[string, string]> = [
  ["mon", "Monday"],
  ["tue", "Tuesday"],
  ["wed", "Wednesday"],
  ["thu", "Thursday"],
  ["fri", "Friday"],
  ["sat", "Saturday"],
  ["sun", "Sunday"],
];

type DayState = { open: boolean; start: string; end: string };

function toDayState(hours: HealthcareSettings["businessHours"]): { always: boolean; days: Record<string, DayState> } {
  const days: Record<string, DayState> = {};
  for (const [key] of DAYS) {
    const h = hours?.[key];
    days[key] = h ? { open: true, start: h.start, end: h.end } : { open: !hours && ["mon", "tue", "wed", "thu", "fri"].includes(key), start: "09:00", end: "17:00" };
  }
  return { always: !hours || Object.keys(hours).length === 0, days };
}

function zoneList(current: string): string[] {
  let zones: string[] = [];
  try {
    zones = (Intl as any).supportedValuesOf("timeZone");
  } catch {
    zones = COUNTRIES.map((c) => c.tz);
  }
  return zones.includes(current) ? zones : [current, ...zones];
}

export default function ClinicPanel({ settings }: { settings: HealthcareSettings | null }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [clinicName, setClinicName] = useState("");
  const [country, setCountry] = useState("AU");
  const [timezone, setTimezone] = useState("Australia/Sydney");
  const [transferNumber, setTransferNumber] = useState("");
  const [emergencyNumber, setEmergencyNumber] = useState("");
  const [hours, setHours] = useState(toDayState(null));
  const [confirmationSms, setConfirmationSms] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setClinicName(settings.clinicName);
    setCountry(settings.country);
    setTimezone(settings.timezone);
    setTransferNumber(settings.transferNumber ?? "");
    setEmergencyNumber(settings.emergencyNumber ?? "");
    setHours(toDayState(settings.businessHours));
    setConfirmationSms(settings.confirmationSms ?? false);
  }, [settings]);

  const zones = useMemo(() => zoneList(timezone), [timezone]);
  const countryInfo = COUNTRIES.find((c) => c.code === country);

  function pickCountry(code: string) {
    setCountry(code);
    // A new account has nothing to lose; for a saved one only the time zone follows, and only if it was the old default.
    const next = COUNTRIES.find((c) => c.code === code);
    if (next && (!settings || timezone === countryInfo?.tz)) setTimezone(next.tz);
  }

  const save = useMutation({
    mutationFn: async () => {
      const businessHours = hours.always
        ? null
        : Object.fromEntries(DAYS.map(([key]) => [key, hours.days[key].open ? { start: hours.days[key].start, end: hours.days[key].end } : null]));
      const res = await apiRequest("PUT", "/api/healthcare/settings", {
        clinicName,
        country,
        timezone,
        transferNumber,
        emergencyNumber,
        businessHours,
        confirmationSms,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Clinic details saved" });
      queryClient.invalidateQueries({ queryKey: ["/api/healthcare/settings"] });
    },
    onError: (e: any) => toast({ title: "Could not save", description: e.message, variant: "destructive" }),
  });

  const setDay = (key: string, patch: Partial<DayState>) => setHours((h) => ({ ...h, days: { ...h.days, [key]: { ...h.days[key], ...patch } } }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Clinic details</CardTitle>
        <CardDescription>The receptionist uses these on every call: how it introduces the clinic, which time zone it speaks in, and when it can hand over to your front desk.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="hc-name">Clinic name</Label>
            <Input id="hc-name" value={clinicName} maxLength={120} onChange={(e) => setClinicName(e.target.value)} placeholder="Harbour Family Clinic" data-testid="input-clinic-name" />
          </div>
          <div className="space-y-2">
            <Label>Country</Label>
            <Select value={country} onValueChange={pickCountry}>
              <SelectTrigger data-testid="select-clinic-country">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Time zone</Label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger data-testid="select-clinic-timezone">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {zones.map((z) => (
                  <SelectItem key={z} value={z}>
                    {z}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Appointment times are read to callers in this zone.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="hc-transfer">Front desk number (for live transfer)</Label>
            <Input id="hc-transfer" value={transferNumber} onChange={(e) => setTransferNumber(e.target.value)} placeholder="+61290001234" data-testid="input-transfer-number" />
            <p className="text-xs text-muted-foreground">International format. Leave empty to take messages only. Transfers happen only during opening hours.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="hc-emergency">Emergency number to tell callers</Label>
            <Input id="hc-emergency" value={emergencyNumber} maxLength={5} onChange={(e) => setEmergencyNumber(e.target.value)} placeholder={countryInfo?.emergency ?? "112"} data-testid="input-emergency-number" />
            <p className="text-xs text-muted-foreground">Leave empty to use {countryInfo?.emergency ?? "the local number"} for {countryInfo?.name ?? "your country"}.</p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <p className="text-sm font-medium">Text a confirmation after booking</p>
              <p className="text-xs text-muted-foreground">Sends the caller an SMS with the doctor, day and time after a booking, change or cancellation. Needs an SMS-enabled number; the text carries no visit reason.</p>
            </div>
            <Switch checked={confirmationSms} onCheckedChange={setConfirmationSms} data-testid="switch-confirmation-sms" />
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <p className="text-sm font-medium">Answer as if always open</p>
              <p className="text-xs text-muted-foreground">Turn off to set opening hours. Outside them the receptionist takes messages instead of transferring.</p>
            </div>
            <Switch checked={hours.always} onCheckedChange={(always) => setHours((h) => ({ ...h, always }))} data-testid="switch-always-open" />
          </div>
          {!hours.always && (
            <div className="space-y-2">
              {DAYS.map(([key, label]) => (
                <div key={key} className="flex flex-wrap items-center gap-3">
                  <div className="flex w-36 items-center gap-2">
                    <Switch checked={hours.days[key].open} onCheckedChange={(open) => setDay(key, { open })} aria-label={`${label} open`} />
                    <span className="text-sm">{label}</span>
                  </div>
                  {hours.days[key].open ? (
                    <div className="flex items-center gap-2">
                      <Input type="time" className="w-32" value={hours.days[key].start} onChange={(e) => setDay(key, { start: e.target.value })} />
                      <span className="text-sm text-muted-foreground">to</span>
                      <Input type="time" className="w-32" value={hours.days[key].end} onChange={(e) => setDay(key, { end: e.target.value })} />
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">Closed</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <Button onClick={() => save.mutate()} disabled={save.isPending || !clinicName.trim()} data-testid="button-save-clinic">
          {save.isPending ? "Saving…" : "Save clinic details"}
        </Button>
      </CardContent>
    </Card>
  );
}
